import type { PendingApproval } from '../../../hooks/queries/useChatStream'
import type {
  ApprovalRequestCall,
  ApprovalTarget,
  MessageBlocks,
} from '../../../app/modules/types/chatBot'
import { isApprovalRequestBlock } from '../../../app/modules/types/chatBot'
import type { ApprovalDecision } from '../../../services/api/chat.api'

/**
 * Max length the service accepts on `denial_message` (§12:837). Longer is a
 * 400, so this module caps rather than lets the request fail.
 *
 * Exported so the deny textarea can cap its own input at the same number
 * instead of hardcoding a second 500 that drifts from this one.
 *
 * The unit is UTF-16 code units, matching `String.length` — which is what the
 * service counts too, so a value that fits here fits there. Persian text is
 * one code unit per character, so this is a true character count for the
 * languages this app ships in.
 */
export const DENIAL_MESSAGE_MAX = 500

/**
 * One approval card's worth of state, whatever it came from.
 *
 * The live stream event and the persisted message block carry the SAME values
 * under different keys (event `id`/`name` vs block `tool_call_id`/`tool_name`
 * — §12:788-799 tabulates the pairs). We normalise onto the block's names
 * because only the block can express a decision, and a reloaded card must
 * render identically to a live one.
 */
export interface ApprovalCardState {
  state: 'open' | 'resolved'
  /**
   * `readonly` because the persisted path hands back react-query's own array
   * by reference (see `fromMessageBlocks`). An in-place `sort` or `splice`
   * here would rewrite cached server data with no re-render to show for it —
   * a corruption that surfaces later, somewhere else. Copy before reordering.
   */
  calls: readonly ApprovalRequestCall[]
}

/** Looks a session id up among the caller's own live sessions. */
export type TargetResolver = (sessionId: string) => ApprovalTarget | null

/**
 * The target for a call the service did not resolve one for.
 *
 * `args` is a wire value of type `unknown` in the live path, so the shape is
 * tested rather than assumed — this runs inside a render and must degrade to
 * "no target", never throw.
 *
 * Callers below differ in whether the key ends up present-as-`null` or
 * absent when there is nothing to fill it with — `fromStreamApprovals`
 * always sets it, `fromMessageBlocks` only touches a call that needs
 * filling. Both are valid readings of `target?: ApprovalTarget | null`, no
 * consumer can tell them apart, and neither is meaningful on its own: do not
 * start treating "key absent" as a distinct state from "key null".
 */
function resolvedTarget(args: unknown, resolveTarget?: TargetResolver): ApprovalTarget | null {
  if (!resolveTarget || !args || typeof args !== 'object') return null
  const sessionId = (args as { session_id?: unknown }).session_id
  return typeof sessionId === 'string' ? resolveTarget(sessionId) : null
}

/**
 * Normalise live `approval_required` approvals. They are always undecided:
 * the event announces the halt, and no decision exists until the user makes
 * one and a refetch brings back the block that records it.
 *
 * `null` for an empty list so a caller can write `card && <ApprovalCard …>`
 * instead of separately testing the array's length.
 *
 * `resolveTarget` is optional because the event itself never carries a
 * target — it is emitted by the generic agent runner, which knows nothing
 * about SSH sessions — so every live call is filled from the caller's own
 * session listing, or left `null` when there is none to consult.
 */
export function fromStreamApprovals(
  approvals: PendingApproval[],
  resolveTarget?: TargetResolver,
): ApprovalCardState | null {
  if (approvals.length === 0) return null
  return {
    state: 'open',
    calls: approvals.map((approval) => ({
      tool_call_id: approval.toolCallId,
      tool_name: approval.toolName,
      args: approval.args,
      decision: null,
      denial_message: null,
      target: resolvedTarget(approval.args, resolveTarget),
    })),
  }
}

/**
 * Pull the approval_request block off a persisted message. This is the
 * authoritative source (§12 step 3): the block is written in the same
 * transaction as the assistant message, so it survives a reload where stream
 * state does not — and unlike the stream it carries `decision` and
 * `denial_message`.
 *
 * The optional chain on `.blocks` is deliberate despite the type saying the
 * array is always there. This is a wire boundary: `MessageBlocks` describes
 * what the service promises, not what actually arrived, and a missing array
 * here would throw inside a message-list render rather than degrade to "no
 * card".
 *
 * `calls` comes back BY REFERENCE, so its identity is stable for as long as
 * the cache entry is — whereas `fromStreamApprovals` allocates a fresh array
 * on every call. The two paths therefore differ in exactly the way that trips
 * up memoization: depend on the card, not on `card.calls`, or the stream path
 * recomputes every render while the persisted one looks fine. `resolveTarget`
 * does not change this: a block is only copied when the resolver actually
 * fills in a target that was missing — a block already fully resolved, or one
 * whose missing target the resolver still cannot answer (no live session yet,
 * most commonly), is passed straight through instead.
 *
 * Returns an ARRAY. It used to `.find()`, which silently dropped every block
 * after the first — unreachable while the service sends at most one per
 * message, but a drop with no symptom is exactly the kind that survives a
 * contract change. An empty array means no card.
 */
export function fromMessageBlocks(
  blocks: MessageBlocks | undefined,
  resolveTarget?: TargetResolver,
): ApprovalCardState[] {
  const found = blocks?.blocks?.filter(isApprovalRequestBlock) ?? []

  return found.map((block) => {
    if (!resolveTarget) return { state: block.state, calls: block.calls }

    // Most calls resolve to nothing new: `start_patch_session` has no session
    // to name yet, and a non-session tool carries no `session_id` at all — so
    // testing "did the resolver actually produce a target" per call, rather
    // than "does this call merely lack one", is what keeps the common case
    // from rebuilding the array (and breaking the by-reference identity this
    // function's contract promises) every render for no change at all.
    let changed = false
    const calls = block.calls.map((call) => {
      // A block's own target always wins: it records what the card meant when
      // it was proposed, and the session it names may since have ended and
      // dropped out of the live listing the resolver consults.
      if (call.target) return call
      const target = resolvedTarget(call.args, resolveTarget)
      if (target === null) return call
      changed = true
      return { ...call, target }
    })

    return { state: block.state, calls: changed ? calls : block.calls }
  })
}

/**
 * Every call on the card has a decision recorded.
 *
 * This is a WHOLE-CARD predicate, and each of the three states a card can be
 * in has its own source (§12:806-809):
 *
 * - a per-call button's disabled state -> that call's own `decision !== null`,
 *   never this. On a multi-call card this stays false until the last call is
 *   decided, so wiring it per-call leaves already-decided calls re-clickable.
 * - the batch submit / "approve all" control -> this.
 * - the card's "done" styling -> `card.state === 'resolved'`, which the
 *   backend sets only once the approved work has actually EXECUTED. A
 *   fully-decided card sits `open` for the few seconds the tools run, which is
 *   why this predicate and `state` are not interchangeable.
 *
 * Vacuously true for a card with no calls. The service never emits an
 * `approval_request` block without one, and `fromStreamApprovals` returns
 * `null` rather than an empty card, so this is unreachable in practice — but
 * "nothing is awaiting a decision" is the right reading of it either way.
 */
export function isFullyDecided(card: ApprovalCardState): boolean {
  return card.calls.every((call) => call.decision !== null)
}

export interface DecisionInput {
  tool_call_id: string
  approved: boolean
  reason?: string
}

/**
 * Build the request body for `POST /chat/{id}/approvals` (§12:811-838).
 *
 * `denial_message` is only meaningful on a denial and is capped server-side at
 * 500 characters, so we drop it on approvals and on blank reasons rather than
 * sending an empty string the model would read as a reason. An over-long
 * reason is truncated, not rejected: the alternative is a 400 the user cannot
 * act on from a render path. The deny textarea should cap input at
 * {@link DENIAL_MESSAGE_MAX} too, so this stays a backstop and the user is
 * never silently cut off mid-sentence.
 *
 * NOT validated here, on purpose: §12:836 also requires a non-empty batch
 * whose `tool_call_id`s all belong to the same assistant message. Neither is
 * checkable from this input — a flat list of ids says nothing about which
 * message they came from — and both are structurally guaranteed by the only
 * caller shape that exists: an `ApprovalCardState` is one block off one
 * message, and a card with no calls never renders. Enforcing them here would
 * mean either a throw on a render path or a silent empty POST; the caller is
 * where the invariant actually lives.
 */
export function buildDecisions(inputs: DecisionInput[]): ApprovalDecision[] {
  // The `: ApprovalDecision` on the CALLBACK is load-bearing, not decoration.
  // Without it the literals below are only checked structurally against the
  // function's declared return type, and `denial_message` is optional — so a
  // typo or rename (`denialMessage`) is an excess property on a non-fresh
  // `.map` result and compiles clean, reaching the server as a denial with no
  // reason attached. Annotated here, the literals are fresh and the rename is
  // a compile error. Without the annotation this drift is silent.
  return inputs.map(({ tool_call_id, approved, reason }): ApprovalDecision => {
    const trimmed = reason?.trim()
    if (approved || !trimmed) return { tool_call_id, approved }
    return { tool_call_id, approved, denial_message: trimmed.slice(0, DENIAL_MESSAGE_MAX) }
  })
}
